import { create } from 'zustand';

interface User {
  id: number;
  email: string;
  username: string;
  role: 'admin' | 'customer' | 'customer_manager' | 'supplier' | 'employee' | 'support' | 'delivery';
  customerId?: number | null;
  supplierId?: number | null;
  inn?: string | null;
  fullName?: string | null;
  phone?: string | null;
  companyName?: string | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  hydrated: boolean;
  setAuth: (user: User, token: string) => void;
  updateUser: (user: User) => void;
  logout: () => void;
  initialize: () => Promise<void>;
  checkDocumentAcceptance: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  hydrated: false,
  
  setAuth: (user: User, token: string) => {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token', token);
    // Reset view mode so previous user's "employee view" does not leak into new session
    localStorage.removeItem('viewMode');
    localStorage.removeItem('supportGuestSession');
    set({ user, token, hydrated: true });
  },
  
  updateUser: (user: User) => {
    localStorage.setItem('user', JSON.stringify(user));
    set({ user });
  },
  
  logout: () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('viewMode');
    localStorage.removeItem('supportGuestSession');
    set({ user: null, token: null, hydrated: true });
  },

  initialize: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      set({ user: null, token: null, hydrated: true });
      return;
    }

    // Do not hydrate UI from cached localStorage user — wait for server.
    set({ user: null, token, hydrated: false });

    try {
      const { authAPI } = await import('../services/api');
      const updatedUser = await authAPI.me();
      localStorage.setItem('user', JSON.stringify(updatedUser));
      set({ user: updatedUser, token, hydrated: true });
    } catch (error) {
      console.error('Failed to fetch user data from server:', error);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('viewMode');
      localStorage.removeItem('supportGuestSession');
      set({ user: null, token: null, hydrated: true });
    }
  },

  checkDocumentAcceptance: async () => {
    const { user } = useAuthStore.getState();
    if (!user) return false;
    
    try {
      const { policyDocumentsAPI } = await import('../services/api');
      const acceptance = await policyDocumentsAPI.checkAcceptance(user.role);
      return acceptance.allAccepted;
    } catch (error) {
      console.error('Check document acceptance error:', error);
      // Fail-open: do not block the UI if the check endpoint is temporarily unavailable.
      return true;
    }
  },
}));

